import { useEffect, useState, useCallback } from "react";
import { listingCoords } from "@/lib/geo";

// Haversine — returns km between two [lat,lng] points.
export function distanceKm(a, b) {
  if (!a || !b) return null;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const s = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(s));
}

// Human-readable string. < 1km → "X min walk", < 50km → "X km", else "X,000 km".
export function formatDistance(km) {
  if (km == null) return null;
  if (km < 1) {
    const mins = Math.max(1, Math.round((km / 5) * 60)); // 5 km/h walk
    return `${mins} min walk`;
  }
  if (km < 10) return `${km.toFixed(1)} km away`;
  if (km < 100) return `${Math.round(km)} km away`;
  if (km < 10000) return `${Math.round(km).toLocaleString()} km away`;
  return `${Math.round(km / 1000)}k km away`;
}

export function listingDistanceFrom(listing, origin) {
  const c = listingCoords(listing);
  if (!c || !origin) return null;
  return distanceKm(origin, c);
}

const STORAGE_KEY = "afm_user_location_v1";

/**
 * Pull-once user location with localStorage cache (15-minute TTL).
 * - `coords` is [lat,lng] or null
 * - call `request()` to trigger the browser permission dialog
 * - call `clear()` to forget the stored location
 */
export function useUserLocation() {
  const [coords, setCoords] = useState(null);
  const [state, setState] = useState("idle"); // idle | requesting | granted | denied | unsupported

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const { lat, lng, ts } = JSON.parse(raw);
      if (Date.now() - ts < 15 * 60 * 1000 && Number.isFinite(lat) && Number.isFinite(lng)) {
        setCoords([lat, lng]);
        setState("granted");
      }
    } catch { /* ignore */ }
  }, []);

  const request = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setState("unsupported");
      return;
    }
    setState("requesting");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = [pos.coords.latitude, pos.coords.longitude];
        setCoords(c);
        setState("granted");
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ lat: c[0], lng: c[1], ts: Date.now() }));
        } catch { /* ignore */ }
      },
      () => setState("denied"),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
  }, []);

  const clear = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setCoords(null);
    setState("idle");
  }, []);

  return { coords, state, request, clear };
}
